const segmenter =
  Intl.Segmenter && new Intl.Segmenter('en', { granularity: 'word' });
const collator = new Intl.Collator('en', {
  usage: 'search',
  sensitivity: 'base',
  ignorePunctuation: false,
});
const matchWhitespace = /^\s+$/s;
const matchSingleSmartQuote = /[‘’]/g;
const matchDoubleSmartQuote = /[“”]/g;

export function wordIndexFromText(text = '', extraMatchInfo) {
  // If the segmenter is not available then fail.
  // https://caniuse.com/mdn-javascript_builtins_intl_segmenter_segment
  const index = Array.from(segmenter?.segment(text) || [], (segment) => {
    const segmentText = segment.segment;
    if (matchWhitespace.test(segmentText)) {
      return {
        ...extraMatchInfo,
        ...segment,
        matchText: ' ',
      };
    }
    let normalizedText = segmentText
      .replace(matchSingleSmartQuote, "'")
      .replace(matchDoubleSmartQuote, '"');
    return {
      ...extraMatchInfo,
      ...segment,
      matchText: normalizedText,
    };
  });
  // console.debug({ wordIndexFromText: text, index });
  return index;
}

// Format of results:
// {
//   searchedText: "Some text containing the term 'defined term'",
//   matches: [
//     {
//       searchedText: "Some text containing the term 'defined term'",
//       searchedTextMatchOffset: 31,
//       searchedTextMatchLength: 12,
//       searchedSegments: [...], // from selectWordIndexForClauseBody()
//       searchedSegmentOffset: 10,
//       termSegments: [         // from selectDefinitionsIndexedForSearch
//         {
//           segment: "Defined",
//           matchText: "Defined",
//           index: 1,
//           input: " Defined  Term ",
//           isWordLike: true,
//           clauseId: "<Definition ID>",
//         }
//         {
//           segment: "  ",
//           matchText: " ",
//           index: 8,
//           input: " Defined  Term ",
//           isWordLike: false,
//           clauseId: "<Definition ID>",
//         }
//         {
//           segment: "Term",
//           matchText: "Term",
//           index: 10,
//           input: " Defined  Term ",
//           isWordLike: true,
//           clauseId: "<Definition ID>",
//         }
//       ]
//     }
//   ],
//   matchesByClauseId: Map({
//     "<Definition ID> ": [...just the matches for <Definition ID>],
//     ...
//   })
// }
export function searchForDefinitionTermMatches(
  indexedTerms = [],
  indexedText = [],
) {
  const matches = [];
  let matchesByClauseId = new Map();
  let segmentIndex = 0;
  const searchedText = indexedText[0]?.input;
  while (segmentIndex < indexedText.length) {
    const remainingSegments = indexedText.length - segmentIndex;
    let match = null;
    const textOffset = indexedText[segmentIndex].index;
    for (const term of indexedTerms) {
      if (term.length <= remainingSegments) {
        if (
          term.every(
            (termSegment, termSegmentIndex) =>
              collator.compare(
                termSegment.matchText,
                indexedText[segmentIndex + termSegmentIndex].matchText,
              ) === 0,
          )
        ) {
          match = {
            searchedText,
            searchedTextMatchOffset: textOffset,
            searchedTextMatchLength: term.reduce(
              (totalLength, termSegment, termSegmentIndex) =>
                totalLength +
                indexedText[segmentIndex + termSegmentIndex].segment.length,
              0,
            ),
            searchedSegments: indexedText,
            searchedSegmentOffset: segmentIndex,
            termSegments: term,
          };
          break;
        }
      }
    }
    if (match) {
      matches.push(match);
      const clauseId = match.termSegments[0]?.clauseId;
      if (clauseId) {
        matchesByClauseId.set(clauseId, [
          ...(matchesByClauseId.get(clauseId) || []),
          match,
        ]);
      }
      // skip over the match and continue searching
      segmentIndex += match.termSegments.length;
    } else {
      segmentIndex += 1;
    }
  }
  const result = {
    searchedText,
    matches,
    matchesByClauseId,
  };
  // console.debug({ searchForDefinitionTermMatches: result });
  return result;
}
